import React, { useEffect, useState } from 'react';
import CountUp from './CountUp';
import { Star } from 'lucide-react';
import { API } from '../utils/apiBase';

/**
 * HeroStats — live social-proof strip for the homepage hero.
 *
 * Pulls /api/reviews/summary and animates a count-up on mount.
 * Falls back to hidden when there are no reviews so we never show
 * fake numbers.
 *
 * Renders as 3 stat tiles to slot inline with the existing hero column.
 */
const HeroStats = ({ tone = 'dark' }) => {
  // tone='dark' = used on dark hero backgrounds (light text)
  const [data, setData] = useState({ count: 0, average: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/reviews/summary`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { setData({ count: d?.count || 0, average: d?.average || 0 }); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const text = tone === 'dark' ? 'text-[#F5EDDD]' : 'text-[#33200F]';
  const sub = tone === 'dark' ? 'text-[#E3D5B8]' : 'text-[#A07A4F]';
  const border = tone === 'dark' ? 'border-[#F5EDDD]/30' : 'border-[#C9B596]';

  const stats = [
    {
      label: 'Average rating',
      render: () => (
        <span className="inline-flex items-baseline gap-1">
          <CountUp end={data.average} duration={1600} decimals={1} className={`text-lg md:text-2xl ${text}`} />
          <Star className={`h-3 w-3 fill-current ${text}`} strokeWidth={1.2} />
        </span>
      ),
    },
    {
      label: data.count === 1 ? 'Verified review' : 'Verified reviews',
      render: () => (
        <CountUp end={data.count} duration={1600} className={`text-lg md:text-2xl ${text}`} />
      ),
    },
    {
      label: 'Year warranty',
      render: () => (
        <CountUp end={2} duration={1600} className={`text-lg md:text-2xl ${text}`} />
      ),
    },
  ];

  return (
    <ul
      className={`mt-6 md:mt-8 grid grid-cols-3 gap-2 md:gap-5 max-w-[200px] md:max-w-md animate-[fadeUp_1100ms_ease-out_620ms_both] ${text}`}
      data-testid="hero-stats"
    >
      {stats.map((s, i) => (
        <li key={i} className={`border-l ${border} pl-2 md:pl-3`}>
          <p style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {s.render()}
          </p>
          <p className={`text-[8px] md:text-[9px] tracking-[0.16em] md:tracking-[0.28em] uppercase ${sub} mt-1 leading-tight`}>{s.label}</p>
        </li>
      ))}
    </ul>
  );
};

export default HeroStats;
